import { useMemo, useState } from 'react';
import { Check, ClipboardList } from 'lucide-react';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { unitOptionsFor } from '@/lib/constants';
import { priceAtMargin } from '@/lib/pricing';
import { cn, formatCurrency } from '@/lib/utils';

/** Margin tiers offered as one-click suggestions. Anything else goes in Custom. */
const MARGIN_PRESETS = [20, 25, 30, 35] as const;

/** Everything the parent needs to write the calculator's result onto the form. */
export interface CostCalculatorResult {
  /** 'case' | 'pack' | … — the bulk unit the supplier cost is quoted in. */
  unitType: string;
  /** Base units per bulk unit, e.g. 12 eaches in a case. */
  unitsInCase: number;
  /** Supplier cost + additional cost, for one bulk unit. */
  bulkUnitCost: number;
  baseUnitCost: number;
  baseUnitSellingPrice: number;
  /** Selling price for one whole case/pack; null when the unit type is 'each'. */
  bulkUnitSellingPrice: number | null;
  /** The gross margin the prices were derived at. */
  margin: number;
}

export interface CostCalculatorInitialValues {
  unitType?: string;
  costPerCase?: string;
  unitsInCase?: string;
}

/**
 * One suggested price: clicking the row selects that margin; "Apply to Product"
 * in the footer then writes it. Defined at module scope — declaring it inside
 * the component would create a new component type on every render, remounting
 * the row and dropping focus from the Custom input as you type.
 */
function PriceRow({
  label,
  price,
  onSelect,
  selected,
}: {
  label: React.ReactNode;
  price: number | null;
  onSelect: () => void;
  selected: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        'flex w-full items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors',
        selected ? 'border-primary bg-primary/5' : 'border-transparent hover:bg-muted/40',
      )}
    >
      <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
        {label}
        {selected && <Check className="h-3.5 w-3.5 text-primary" />}
      </span>
      <span className="text-base font-semibold tabular-nums text-foreground">
        {price != null ? formatCurrency(price) : '$--'}
      </span>
    </button>
  );
}

/**
 * Cost Calculator — works out a base unit cost from a supplier's case price,
 * then offers selling prices at a range of gross margins.
 *
 * Laid out as two columns: the inputs and resulting unit cost on the left, the
 * suggested selling prices on the right. Pick a margin on the right, review
 * what it will write in the footer, then "Apply to Product".
 */
export function CostCalculatorModal({
  open,
  onClose,
  initial,
  onApply,
}: {
  open: boolean;
  onClose: () => void;
  initial?: CostCalculatorInitialValues;
  onApply: (result: CostCalculatorResult) => void;
}) {
  const [unitType, setUnitType] = useState(initial?.unitType || 'case');
  const [costPerCase, setCostPerCase] = useState(initial?.costPerCase || '');
  const [additionalCost, setAdditionalCost] = useState('');
  const [unitsInCase, setUnitsInCase] = useState(initial?.unitsInCase || '');
  // Single source of truth for the chosen margin. A preset row reads as selected
  // when it equals this, and the Custom field shows it when it matches no preset
  // — so there is no second piece of state to fall out of sync.
  const [margin, setMargin] = useState('');

  const unitLabel = unitType.charAt(0).toUpperCase() + unitType.slice(1);
  const isBulk = unitType !== 'each';

  const bulkUnitCost = useMemo(() => {
    const base = parseFloat(costPerCase);
    if (!Number.isFinite(base) || base <= 0) return null;
    return base + (parseFloat(additionalCost) || 0);
  }, [costPerCase, additionalCost]);

  const baseUnitCost = useMemo(() => {
    const units = parseFloat(unitsInCase);
    if (bulkUnitCost == null || !Number.isFinite(units) || units <= 0) return null;
    return bulkUnitCost / units;
  }, [bulkUnitCost, unitsInCase]);

  const marginNum = parseFloat(margin);
  const activeMargin = Number.isFinite(marginNum) ? marginNum : null;
  const sellingPrice = baseUnitCost != null && activeMargin != null ? priceAtMargin(baseUnitCost, activeMargin) : null;
  const bulkSellingPrice = useMemo(() => {
    const units = parseFloat(unitsInCase);
    if (sellingPrice == null || !isBulk || !Number.isFinite(units) || units <= 0) return null;
    return sellingPrice * units;
  }, [sellingPrice, unitsInCase, isBulk]);

  const customValue = MARGIN_PRESETS.some((p) => p === marginNum) ? '' : margin;

  const apply = (atMargin: number) => {
    const units = parseFloat(unitsInCase);
    const price = baseUnitCost != null ? priceAtMargin(baseUnitCost, atMargin) : null;
    if (baseUnitCost == null || bulkUnitCost == null || price == null || !Number.isFinite(units) || units <= 0) return;
    onApply({
      unitType,
      unitsInCase: units,
      bulkUnitCost,
      baseUnitCost,
      baseUnitSellingPrice: price,
      bulkUnitSellingPrice: isBulk ? price * units : null,
      margin: atMargin,
    });
  };

  const inputCls =
    'h-9 w-full rounded-lg border border-input px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary';
  const labelCls = 'mb-1 block text-xs font-medium text-muted-foreground';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Cost Calculator"
      subtitle="Enter a supplier cost and the number of base units to see suggested prices."
      widthClass="max-w-[860px]"
      footer={
        <div className="flex flex-wrap items-center justify-end gap-3">
          {/* What "Apply to Product" will write, spelled out — the case price in
              particular is easy to miss, and it lands on the Case/Pack row. */}
          {sellingPrice != null && baseUnitCost != null && (
            <div className="min-w-0 flex-1 text-xs text-muted-foreground">
              Applies cost <span className="font-medium text-foreground">{formatCurrency(baseUnitCost)}</span> and price{' '}
              <span className="font-medium text-foreground">{formatCurrency(sellingPrice)}</span> per base unit at{' '}
              <span className="font-medium text-foreground">{activeMargin}%</span> margin
              {bulkSellingPrice != null && (
                <>
                  , and <span className="font-medium text-foreground">{formatCurrency(bulkSellingPrice)}</span> per{' '}
                  {unitType}
                </>
              )}
              .
            </div>
          )}
          {/* Actions stay pinned to the right whether or not the summary is showing. */}
          <div className="ml-auto flex shrink-0 items-center gap-2">
            <Button variant="secondary" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={sellingPrice == null || activeMargin == null}
              onClick={() => activeMargin != null && apply(activeMargin)}
            >
              Apply to Product
            </Button>
          </div>
        </div>
      }
    >
      <div className="grid gap-6 md:grid-cols-2 md:divide-x md:divide-border">
        {/* ── Inputs ─────────────────────────────────────────────── */}
        <div className="space-y-4 md:pr-6">
          <div>
            <label className={labelCls}>Unit Type</label>
            <select value={unitType} onChange={(e) => setUnitType(e.target.value)} className={inputCls}>
              {unitOptionsFor(unitType).map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelCls}>Supplier Cost per {unitLabel} ($)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={costPerCase}
              onChange={(e) => setCostPerCase(e.target.value)}
              placeholder={`Enter cost per ${unitType}`}
              className={inputCls}
            />
          </div>

          <div>
            <label className={labelCls}>Number of Base Units in a {unitLabel}</label>
            <input
              type="number"
              step="1"
              min="1"
              value={unitsInCase}
              onChange={(e) => setUnitsInCase(e.target.value)}
              placeholder="Enter units"
              className={inputCls}
            />
          </div>

          <div>
            <label className={labelCls}>
              Additional Cost ($){' '}
              <span
                title="Shipping, handling, or other costs for the whole case — spread across the base units"
                className="ml-0.5 inline-flex h-4 w-4 cursor-help items-center justify-center rounded-full bg-muted text-[10px] text-muted-foreground"
              >
                i
              </span>
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={additionalCost}
              onChange={(e) => setAdditionalCost(e.target.value)}
              placeholder="Enter additional cost"
              className={inputCls}
            />
          </div>

          {/* The number everything on the right is derived from, so it reads as
              the result of this column rather than another field. */}
          <div className="rounded-lg border border-border bg-muted/30 px-4 py-3">
            <div className="text-xs font-medium text-muted-foreground">Calculated Base Unit Cost</div>
            <div className="mt-1 flex items-center gap-2">
              <span className="text-2xl font-bold tabular-nums text-foreground">
                {baseUnitCost != null ? formatCurrency(baseUnitCost) : '$--'}
              </span>
              {baseUnitCost != null && (
                <button
                  type="button"
                  onClick={() => navigator.clipboard.writeText(baseUnitCost.toFixed(2))}
                  className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  title="Copy base unit cost"
                  aria-label="Copy base unit cost"
                >
                  <ClipboardList className="h-4 w-4" />
                </button>
              )}
            </div>
            {bulkUnitCost != null && isBulk && (
              <div className="mt-1 text-xs text-muted-foreground">
                {formatCurrency(bulkUnitCost)} per {unitType} ÷ {unitsInCase || '—'} units
              </div>
            )}
          </div>
        </div>

        {/* ── Suggested selling prices ───────────────────────────── */}
        <div className="md:pl-6">
          <div className="mb-1 text-sm font-semibold text-foreground">Suggested Selling Prices</div>
          <p className="mb-3 text-xs text-muted-foreground">
            Price per base unit at each gross margin. Pick one, or enter your own.
          </p>

          <div className="space-y-1">
            {MARGIN_PRESETS.map((p) => (
              <PriceRow
                key={p}
                label={`${p}%`}
                price={baseUnitCost != null ? priceAtMargin(baseUnitCost, p) : null}
                selected={marginNum === p}
                onSelect={() => setMargin(String(p))}
              />
            ))}

            <div className="pt-1">
              <PriceRow
                label={
                  <span className="flex items-center gap-2">
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="99.9"
                      value={customValue}
                      onChange={(e) => setMargin(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      placeholder="Custom"
                      aria-label="Custom margin percentage"
                      className="h-8 w-24 rounded-md border border-input px-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                    />
                    <span className="text-muted-foreground">%</span>
                  </span>
                }
                price={
                  baseUnitCost != null && customValue !== '' && Number.isFinite(marginNum)
                    ? priceAtMargin(baseUnitCost, marginNum)
                    : null
                }
                selected={customValue !== '' && Number.isFinite(marginNum)}
                onSelect={() => {}}
              />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
